import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean, useUpdateEffect } from 'ahooks';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Unstable_Alert2 as Alert,
  AlertTitle,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useLoginWithFido } from '@/components/biz/fido/use-fido';
import { GoogleCaptcha, type GoogleCaptchaRef } from '@/components/biz/google-captcha';
import { FormCheckbox } from '@/components/form/form-checkbox';
import { FormText } from '@/components/form/form-text';
import { StepBox } from '@/components/layout/step-box';
import { MySvgIcon } from '@/components/ui/svg-icon';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import { useBizState } from '@/hooks/use-biz-state';
import { useCoreRequest } from '@/hooks/use-core-request';
import { useCustom } from '@/hooks/use-custom';
import { useEnablePasskeyFlow } from '@/hooks/use-enable-passkey-flow';
import { useFlow } from '@/hooks/use-flow';
import { useLidForm } from '@/hooks/use-lid-form';
import { useRules } from '@/hooks/use-rules';
import { useVerifyUsername } from '@/hooks/use-verify-username';
import { AppCoreContext } from '@/providers/core-provider';
import { useConfigStore } from '@/store/use-config-store';

export interface PasswordValues {
  password: string;
  autoLoginState: boolean;
}

export const StepVerifyPassword: React.FC = () => {
  const { userName, authType, areaCode, enablePassKey, googleVersion, signInBlocked, supportPassKey, setBizState } =
    useBizState();
  const { isPasskeyEnabled } = useCustom();
  const { et } = useConfigStore();
  const [showPassword, { toggle }] = useBoolean();
  const { doEnablePasskeyFlow } = useEnablePasskeyFlow();
  const { lenovoId } = useContext(AppCoreContext);
  const { setFlow } = useFlow();
  const { t, i18n } = useTranslation();
  const { run: fidoRun, loading: fidoRequesting, error } = useLoginWithFido();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const rules = useRules();
  const recaptchaRef = useRef<GoogleCaptchaRef>(null);
  const { doVerifyUserName, loading: verifyUserNameRequesting } = useVerifyUsername(
    'reset',
    recaptchaRef,
    recaptchaToken,
    recaptchaLoading,
  );
  const { control, setError, handleSubmit } = useLidForm<PasswordValues>({
    defaultValues: {
      password: '',
      autoLoginState: true,
    },
  });

  const { run, loading: verifyPasswordRequesting } = useCoreRequest(
    (params: PasswordValues) => {
      setBizState({
        password: params.password,
        autoLoginState: params.autoLoginState,
        signInBlocked: false,
      });

      return lenovoId.loginVerifyPassword({
        userName,
        autoLoginState: params.autoLoginState === true ? '1' : '0',
        password: params.password,
        areaCode: authType === 'phone' ? areaCode : '',
        et,
        bid: window.bid,
        lang: i18n.language,
        googleToken: recaptchaToken,
        googleVersion,
        supportPassKey,
      });
    },
    {
      manual: true,
      ready: !recaptchaLoading,
      onBefore: () => {
        setButtonLoading(true);
      },
      onSuccess: ({ data, nextSteps }) => {
        setButtonLoading(false);
        if (nextSteps === Flow.LOGIN_STEP_ENABLE_PASSKEY) {
          setBizState(data);
          doEnablePasskeyFlow(data);
          return;
        } else if (nextSteps === Flow.LOGIN_STEP_SUCCESS_CALLBACK) {
          setBizState(data);
          if (data.callBack) {
            window.location.href = data.callBack;
            return;
          }
        } else if (nextSteps === Flow.LOGIN_STEP_MFA) {
          setFlow(Flow.LOGIN_STEP_MFA);
          setBizState({ ...data, mfa: data.mfa });
          return;
        } else if (nextSteps === Flow.LOGIN_STEP_ENFORCED_MFA) {
          setFlow(Flow.LOGIN_STEP_ENFORCED_MFA);
          setBizState({ ...data, mfa: data.mfa });
          return;
        } else if (nextSteps === Flow.LOGIN_STEP_AUTHORIZATION) {
          const { authType, ...rest } = data;
          setBizState({
            ...rest,
            oauthGrantAuthType: authType,
          });
          setFlow(Flow.LOGIN_STEP_AUTHORIZATION);
          return;
        }

        setFlow(Flow.EXCEPTION_SOMETHING_WENT_WRONG);
      },
      onError: (code) => {
        setButtonLoading(false);
        switch (code) {
          case ErrorCode.InvalidPasswordFormat:
            setError('password', t('password_format_error'));
            return true;
          case ErrorCode.InvalidUsernameFormat:
          case ErrorCode.UsernameNotExists:
            recaptchaRef.current?.reload();
            setError('password', t('password_incorrect_error'));
            return true;
          case ErrorCode.LoginLocked:
            recaptchaRef.current?.reload();
            setBizState({ signInBlocked: true });
            return true;
          case ErrorCode.UserFrozen:
            setFlow(Flow.LOGIN_STEP_DISABLE_RECOVERY);
            return true;
          case ErrorCode.UserDisabled:
            setFlow(Flow.LOGIN_STEP_DISABLE);
            return true;
        }
      },
    },
  );

  const handleCheckRecaptcha = () => {
    const isValid = recaptchaRef.current?.validation();
    return isValid;
  };

  const submit = handleSubmit((data) => {
    if (handleCheckRecaptcha()) run(data);
  });

  const handleRecaptchaLoading = (loading: boolean) => {
    setRecaptchaLoading(loading);
  };

  const handleRecaptchaToken = (token: string) => {
    setRecaptchaToken(token);
  };

  const handleForgot = () => {
    if (recaptchaRef.current?.validation()) {
      doVerifyUserName('reset');
    }
  };

  const handleBack = () => {
    setFlow(Flow.LOGIN_STEP_INPUT_USERNAME);
  };

  useUpdateEffect(() => {
    if (!recaptchaLoading) {
      setButtonLoading(false);
    }
  }, [recaptchaLoading]);

  return (
    <StepBox
      backFlow={Flow.LOGIN_STEP_INPUT_USERNAME}
      backButtonProps={{
        disabled: fidoRequesting || verifyPasswordRequesting,
      }}
      account={authType === 'phone' ? `${areaCode} ${userName}` : userName}
      title={t('password_input_title')}
    >
      <form onSubmit={submit} noValidate>
        <FormText
          name="password"
          label={t('password')}
          control={control}
          rules={rules.password}
          type={showPassword ? 'text' : 'password'}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? t('hide_password_tooltip') : t('show_password')}
                    onClick={toggle}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseUp={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <FormCheckbox name="autoLoginState" label={t('remember_login_checkbox')} control={control} />

          <Link
            component="button"
            type="button"
            variant="body2"
            color={fidoRequesting || verifyPasswordRequesting ? 'textDisabled' : 'primary'}
            disabled={fidoRequesting || verifyPasswordRequesting}
            className="whitespace-nowrap text-nowrap"
            onClick={handleForgot}
          >
            {t('forgot_password')}
          </Link>
        </Stack>
        {signInBlocked && (
          <Alert severity="error" className="mb-2">
            <AlertTitle>{t('login_blocked_title')}</AlertTitle>
            <div className="ml-0.5 mt-1">
              <Typography variant="body2">{t('login_blocked_message')}</Typography>
              <Link
                sx={{ mt: 2 }}
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={handleBack}
              >
                {t('switch_account_button')}
              </Link>
            </div>
          </Alert>
        )}

        <GoogleCaptcha
          onLoadingChange={handleRecaptchaLoading}
          onTokenChange={handleRecaptchaToken}
          ref={recaptchaRef}
        />

        <Button
          type="submit"
          sx={{ mt: 2 }}
          fullWidth
          variant="contained"
          color="primary"
          disabled={fidoRequesting || verifyUserNameRequesting}
          loading={verifyPasswordRequesting || recaptchaLoading || buttonLoading}
          onClick={handleCheckRecaptcha}
          loadingPosition="start"
        >
          {t('continue')}
        </Button>
      </form>
      {isPasskeyEnabled && enablePassKey && supportPassKey && (
        <div className="mt-2 flex flex-col gap-4">
          <Divider aria-label={t('or_divider')} role="">
            {t('or_divider')}
          </Divider>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            disabled={verifyPasswordRequesting || verifyUserNameRequesting}
            loading={fidoRequesting}
            startIcon={<MySvgIcon name="passkey" aria-hidden="true" width="24" height="24" />}
            onClick={fidoRun}
          >
            {t('passkey_login_button')}
          </Button>

          {error && (
            <Alert severity="error">
              {error.title && <AlertTitle>{t(error.title)}</AlertTitle>}
              {t(error.message)}
            </Alert>
          )}
        </div>
      )}
    </StepBox>
  );
};
